export const PLATFORMS = [
  {
    slug: 'motoinsights',
    name: 'MotoInsights.in',
    tagline: 'Automotive Market Intelligence',
    description:
      '~400M vehicles tracked across 1,500+ RTOs. Market share, demand trends, EV adoption, and dealer network planning powered by AI.',
    url: 'https://motoinsights.in/',
    category: 'Market Intelligence',
    metric: '~400M vehicles · 1,500+ RTOs',
    type: 'built',
  },
  {
    slug: 'dlip',
    name: 'DLIP',
    tagline: 'Dealer Lead Intelligence Platform',
    description:
      'SLA-enforced lead management that protects dealership revenue with auto-escalation, delivered-deal locks, and AI lead scoring.',
    url: 'https://dlip.motoinsights.in/',
    category: 'Dealer Operations',
    metric: 'Lead-to-delivery integrity',
    type: 'invested',
  },
  {
    slug: 'vehicleledger',
    name: 'VehicleLedger.in',
    tagline: 'Unified Vehicle Lifecycle Ledger',
    description:
      'One chassis number links manufacturing, dispatch, dealer stock, finance, insurance, and registration into a single source of truth.',
    url: 'https://vehicleledger.in/',
    category: 'Data Infrastructure',
    metric: 'One chassis. One ledger.',
    type: 'built',
  },
  {
    slug: 'otobot',
    name: 'Oto.bot',
    tagline: 'AI Operating Layer for Automotive Retail',
    description:
      'Copilots for every dealership role (GM, sales, principal, CRM), decision intelligence, and virtual employees for prospecting, reactivation and service adherence — built by Ather-rooted founders.',
    url: 'https://oto.bot/',
    category: 'Dealer AI',
    metric: 'Copilots · Virtual employees',
    type: 'invested',
  },
  {
    slug: 'carbikeclaims',
    name: 'CarBikeClaims.com',
    tagline: 'Automotive Claims Intelligence',
    description:
      "India's first platform connecting vehicle owners, OEMs, and workshops to turn complaints into reliability data and defect intelligence.",
    url: 'https://carbikeclaims.com/',
    category: 'Quality & Claims',
    metric: 'Complaints → defect intelligence',
    type: 'invested',
  },
  {
    slug: 'baywise',
    name: 'BayWise.ai',
    tagline: 'Operating System for Auto Workshops',
    description:
      'AI-powered scheduling and embedded payments for auto repair shops, collision centres and dealer service networks — one login, one subscription, live in 30 minutes. USA · UAE · India · EU.',
    url: 'https://www.baywise.ai/',
    category: 'Workshop OS',
    metric: 'AI scheduling · Embedded payments',
    type: 'invested',
  },
  {
    slug: 'autodata-exchange',
    name: 'AutoData.Exchange',
    tagline: 'Vehicle Lifecycle Data Infrastructure',
    description:
      'Consortium-led neutral data exchange connecting OEMs, insurers, lenders, and service networks through a trusted governance model.',
    url: 'https://autodata.exchange/',
    category: 'Data Exchange',
    metric: 'OEMs · Insurers · Lenders',
    type: 'built',
  },
  {
    slug: 'trucksbuses',
    name: 'TrucksBuses.com',
    tagline: 'Commercial Vehicle Marketplace',
    description:
      "India's one-stop platform to buy and sell new and used trucks, buses, mini trucks, and 3-wheelers — with price discovery, comparisons, dealer locator, and financing tools.",
    url: 'https://www.trucksbuses.com/',
    category: 'CV Marketplace',
    metric: 'CV buy · sell · finance',
    type: 'invested',
  },
  {
    slug: 'autorox',
    name: 'Autorox.ai',
    tagline: 'AI Garage Management OS',
    description:
      'Cloud-based AI workshop platform — digital job cards, predictive inventory, WhatsApp customer flow, GST-ready billing. Trusted by garages in India and 30+ countries.',
    url: 'https://www.autorox.ai/',
    category: 'Workshop & Aftersales',
    metric: '30+ countries · AI Garage OS',
    type: 'built',
  },
  {
    slug: 'autozilla',
    name: 'Autozilla.co',
    tagline: 'B2B Auto Parts Marketplace',
    description:
      "India's B2B spare-parts marketplace connecting manufacturers, distributors, retailers and workshops — OEM + aftermarket catalogue, EPC integration. Bosch-backed.",
    url: 'https://info.autozilla.co/',
    category: 'Aftermarket Commerce',
    metric: 'B2B · OEM + aftermarket',
    type: 'built',
  },
];

// Pan-India automotive ecosystem reference numbers (latest reported, FY26)
// Sources: MoRTH Vahan (~39.77 cr vehicles · 28 Feb 2026), SIAM, FADA FY26 retail,
// PIB EV charger count (1 Mar 2026), NHAI, ACMA, IBEF.
export const STATS = [
  { value: '397M+', label: 'Registered vehicles · India' },
  { value: '29.7M', label: 'New vehicles · FY26 retail' },
  { value: '28K+', label: 'Auto dealer outlets' },
  { value: '80+', label: 'OEMs operating in India' },
];

// More granular numbers used across the Landscape page
export const INDIA_AUTO = {
  registeredVehicles: '397M+',
  newSales: '29.7M',
  twoWheelerShare: '~72%',
  evShareNewSales: '~8.5%',
  rtos: '1,500+',
  dealerOutlets: '28,000+',
  oems: '80+',
  workshops: '1.5M+',
  publicEvChargers: '27,737',
  highwayKm: '6.7M km',
  roadRank: '#2 globally',
  pinCodesServed: '19,300+',
};

export const SOLUTIONS = [
  {
    audience: 'OEMs',
    title: 'Demand, dispatch & defect intelligence',
    points: [
      'Market share & segment-level demand signals',
      'Dispatch-to-registration leakage analytics',
      'Field complaints → engineering feedback loop',
      'EV adoption & competitor benchmarking',
    ],
  },
  {
    audience: 'Dealerships',
    title: 'Lead-to-delivery discipline',
    points: [
      'SLA-enforced lead routing & escalation',
      'AI lead scoring on enquiry intent',
      'Delivered-deal locks against poaching',
      'Showroom & workshop performance lens',
    ],
  },
  {
    audience: 'Insurers & Lenders',
    title: 'Underwriting on real lifecycle data',
    points: [
      'Chassis-linked ownership & service history',
      'Defect-aware risk pricing models',
      'Claims fraud signals from workshop network',
      'Asset-tracking for commercial vehicle finance',
    ],
  },
  {
    audience: 'Fleets & Mobility',
    title: 'Asset, route & resale intelligence',
    points: [
      'CV/EV total-cost-of-ownership models',
      'Resale & residual value benchmarks',
      'Dealer & workshop locator coverage',
      'Financing & insurance partnerships',
    ],
  },
];

export const VENTURES = [
  {
    name: 'TrucksBuses.com',
    stake: 'Operating venture',
    summary:
      "India's commercial vehicle marketplace — new & used trucks, buses, 3-wheelers, with price discovery, finance and dealer network.",
    url: 'https://www.trucksbuses.com/',
    sector: 'Marketplace',
  },
  {
    name: 'Oto.bot',
    stake: 'Operating venture',
    summary:
      'The AI operating layer for automotive retail — copilots for GM/sales/principal/CRM, virtual employees for prospecting, reactivation and service, decision intelligence for dealer principals. Ather-rooted founders.',
    url: 'https://oto.bot/',
    sector: 'Dealer AI',
  },
  {
    name: 'MotoInsights.in',
    stake: 'Operating venture',
    summary:
      'The automotive market intelligence backbone — 397M+ vehicles, 1,500+ RTOs, OEMs, dealers, EV trackers.',
    url: 'https://motoinsights.in/',
    sector: 'Data & Analytics',
  },
  {
    name: 'AutoData.Exchange',
    stake: 'Consortium venture',
    summary:
      'Neutral consortium-led data exchange across OEMs, insurers, lenders, and workshops — governed for trust.',
    url: 'https://autodata.exchange/',
    sector: 'Data Infrastructure',
  },
  {
    name: 'Future bets',
    stake: 'Active scouting',
    summary:
      'EV charging, CV telematics, used-vehicle financing, workshop SaaS, parts commerce, and mobility-as-a-service.',
    url: null,
    sector: 'Thesis-led',
  },
];
